import React, { useState } from 'react';
import type { RuleMatch } from '../../types/frontend';

interface PrimaryRulesSectionProps {
  rules: RuleMatch[]; // Each rule now has its own tokenContributions
  selectedIds?: Set<string>;
  onToggleSelection?: (id: string) => void;
}

/**
 * Highlight tokens and n-grams based on their semantic contribution scores (SHAP-style)
 * Uses gradient coloring: higher contribution = stronger highlight
 * Prioritizes longer n-grams over individual tokens for more accurate attribution
 */
const highlightByContribution = (text: string, contributions: Record<string, number>): React.ReactNode => {
  if (!contributions || Object.keys(contributions).length === 0) return text;

  // Find max contribution for normalization
  const maxContribution = Math.max(...Object.values(contributions));
  if (maxContribution <= 0) return text;

  // Stop words to exclude from highlighting (lone stop words only, not in phrases)
  const stopWords = new Set([
    'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
    'you', 'he', 'she', 'it', 'they', 'we', 'i',
    'of', 'to', 'in', 'for', 'on', 'at', 'by', 'with', 'from',
    'and', 'or', 'but', 'if', 'then'
  ]);

  // Sort n-grams by length (longest first) for greedy matching
  // Filter out lone stop words (single-word stop words)
  const ngramsByLength = Object.entries(contributions)
    .filter(([ngram]) => {
      // Keep multi-word phrases (they contain semantic content)
      if (ngram.split(' ').length > 1) return true;
      // Filter out single-word stop words
      return !stopWords.has(ngram.toLowerCase());
    })
    .sort((a, b) => b[0].split(' ').length - a[0].split(' ').length);

  const highlights: Array<{ start: number; end: number; score: number; text: string }> = [];

  // Greedy matching: find all n-gram occurrences (longest first)
  for (const [ngram, score] of ngramsByLength) {
    // Skip n-grams with zero or negative contribution
    if (score <= 0) continue;

    // Create regex for word boundary matching
    const pattern = new RegExp(`\\b${ngram.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    let match;

    while ((match = pattern.exec(text)) !== null) {
      const index = match.index;
      const end = index + match[0].length;

      // Check if this span overlaps with existing highlights
      const overlaps = highlights.some(h =>
        (index >= h.start && index < h.end) ||
        (end > h.start && end <= h.end) ||
        (index <= h.start && end >= h.end)
      );

      if (!overlaps) {
        highlights.push({ start: index, end, score, text: ngram });
      }
    }
  }

  // Sort highlights by position
  highlights.sort((a, b) => a.start - b.start);

  // Build highlighted output
  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  highlights.forEach((highlight, idx) => {
    // Add unhighlighted text before this highlight
    if (highlight.start > lastIndex) {
      result.push(
        <span key={`text-${idx}`}>
          {text.substring(lastIndex, highlight.start)}
        </span>
      );
    }

    // Add highlighted text
    const intensity = highlight.score / maxContribution;
    const alpha = 0.15 + (intensity * 0.35); // 0.15 to 0.5
    const fontWeight = 400 + Math.round(intensity * 200); // 400 to 600

    result.push(
      <mark
        key={`highlight-${idx}`}
        style={{
          background: `rgba(181, 142, 83, ${alpha})`,
          borderRadius: '2px',
          padding: '0 2px',
          fontWeight,
        }}
        title={`"${highlight.text}" → ${highlight.score.toFixed(3)}`}
      >
        {text.substring(highlight.start, highlight.end)}
      </mark>
    );

    lastIndex = highlight.end;
  });

  // Add any remaining text
  if (lastIndex < text.length) {
    result.push(
      <span key="text-end">
        {text.substring(lastIndex)}
      </span>
    );
  }

  return result;
};

export const PrimaryRulesSection: React.FC<PrimaryRulesSectionProps> = ({ 
  rules,
  selectedIds = new Set(),
  onToggleSelection,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Filter out results below 50% threshold (less than 50% of best match)
  const filteredRules = rules.filter(rule => rule.confidence >= 0.5);

  const INITIAL_DISPLAY_COUNT = 3;
  const displayedRules = showAll ? filteredRules : filteredRules.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = filteredRules.length > INITIAL_DISPLAY_COUNT;

  if (rules.length === 0) {
    return (
      <div className="result-section">
        <div
          className="section-header"
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <span>Applicable omina</span>
          <span className="chevron">{isCollapsed ? '▶' : '▼'}</span>
        </div>
        {!isCollapsed && (
          <div className="section-empty">No matching omina found for your dream.</div>
        )}
      </div>
    );
  }

  return (
    <div className="result-section" data-testid="primary-rules">
      <div
        className="section-header"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <span>Applicable omina</span>
        <span className="chevron">{isCollapsed ? '▶' : '▼'}</span>
      </div>
      {!isCollapsed && (
        <div className="section-content">
          {displayedRules.map((rule) => {
            // Get severity-based styling
            const getSeverityStyle = (sentiment: string, severity?: string | number) => {
              if (severity === undefined || severity === null) return {};

              // Convert numeric severity to category string
              let severityCategory: string;
              if (typeof severity === 'number') {
                const absSeverity = Math.abs(severity);
                if (absSeverity >= 2) {
                  severityCategory = 'strong';
                } else if (absSeverity >= 1) {
                  severityCategory = 'moderate';
                } else {
                  severityCategory = 'light';
                }
              } else {
                severityCategory = severity.toLowerCase();
              }

              const severityLower = severityCategory;

              if (sentiment === 'good') {
                switch (severityLower) {
                  case 'strong':
                    return {
                      background: 'rgba(74, 124, 89, 0.15)',
                      border: '1px solid var(--omen-favorable)',
                      fontWeight: 600,
                    };
                  case 'moderate':
                    return {
                      background: 'rgba(74, 124, 89, 0.1)',
                      border: '1.5px solid var(--omen-favorable)',
                      fontWeight: 500,
                    };
                  case 'light':
                    return {
                      background: 'rgba(74, 124, 89, 0.06)',
                      border: '1px solid rgba(74, 124, 89, 0.3)',
                      fontWeight: 500,
                      opacity: 0.7,
                    };
                  default:
                    return {};
                }
              } else if (sentiment === 'bad') {
                switch (severityLower) {
                  case 'strong':
                    return {
                      background: 'rgba(139, 74, 58, 0.15)',
                      border: '1px solid var(--omen-unfavorable)',
                      fontWeight: 600,
                    };
                  case 'moderate':
                    return {
                      background: 'rgba(139, 74, 58, 0.1)',
                      border: '1.5px solid var(--omen-unfavorable)',
                      fontWeight: 500,
                    };
                  case 'light':
                    return {
                      background: 'rgba(139, 74, 58, 0.06)',
                      border: '1px solid rgba(139, 74, 58, 0.3)',
                      fontWeight: 500,
                      opacity: 0.7,
                    };
                  default:
                    return {};
                }
              }

              return {};
            };

            const getRelevanceLabel = (confidence: number) => {
              const pct = Math.round(confidence * 100);
              return pct === 100 ? 'Best match' : `${pct}% relevance`;
            };

            const isSelected = selectedIds.has(rule.id);
            
            return (
              <div 
                key={rule.id} 
                className="rule-card" 
                style={{ 
                  position: 'relative',
                  transform: isSelected ? 'translateX(12px)' : 'translateX(0)',
                  transition: 'transform 0.2s ease',
                  cursor: onToggleSelection ? 'pointer' : 'default',
                }}
                onClick={() => onToggleSelection?.(rule.id)}
                title={onToggleSelection ? (isSelected ? 'Remove from interpretation' : 'Add to interpretation') : undefined}
              >
                {/* Selection tab on left edge */}
                {onToggleSelection && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '-8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '8px',
                      height: '60%',
                      background: isSelected ? 'var(--lapis-lazuli)' : 'var(--stone-dark)',
                      borderRadius: '4px 0 0 4px',
                      transition: 'background 0.2s ease',
                      opacity: isSelected ? 1 : 0.3,
                    }}
                  />
                )}
                <div className="rule-header">
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--soot)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {getRelevanceLabel(rule.confidence)}
                  </span>
                  {rule.outcomeSentiment && (
                    <span
                      className={`sentiment-badge ${rule.outcomeSentiment.sentiment}`}
                      style={getSeverityStyle(
                        rule.outcomeSentiment.sentiment,
                        rule.outcomeSentiment.severity
                      )}
                      title={
                        rule.outcomeSentiment.severity !== undefined
                          ? `Severity: ${rule.outcomeSentiment.severity}`
                          : undefined
                      }
                    >
                      {rule.outcomeSentiment.sentiment}
                    </span>
                  )}
                </div>

                <p className="rule-condition">
                  <strong>If</strong> {highlightByContribution(rule.condition, rule.tokenContributions || {})}
                </p>

                <p className="rule-outcome">
                  <strong>Then</strong> {rule.outcome}
                </p>

                {rule.matchedSegments && rule.matchedSegments.length > 0 ? (
                  <div
                    style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--dust)',
                      marginTop: 'var(--space-2)',
                    }}
                  >
                    {rule.matchedSegments.length > 1 && (
                      <div style={{ fontWeight: 500, marginBottom: 'var(--space-1)', color: 'var(--soot)' }}>
                        Matched {rule.matchedSegments.length} segments:
                      </div>
                    )}
                    {rule.matchedSegments.map((seg, idx) => {
                      const text = seg.segment.text;
                      const isMultiSegment = rule.matchedSegments!.length > 1;
                      return (
                        <p
                          key={idx}
                          style={{
                            fontStyle: 'italic',
                            marginTop: idx > 0 ? 'var(--space-1)' : 0,
                            paddingLeft: isMultiSegment ? 'var(--space-2)' : 0,
                            borderLeft: isMultiSegment ? '2px solid var(--stone-medium)' : 'none',
                          }}
                        >
                          &ldquo;{text.slice(0, 150)}{text.length > 150 ? '...' : ''}&rdquo;
                        </p>
                      );
                    })}
                  </div>
                ) : rule.sourceSentence ? (
                  <p
                    style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--dust)',
                      fontStyle: 'italic',
                      marginTop: 'var(--space-2)',
                    }}
                  >
                    &ldquo;{rule.sourceSentence}&rdquo;
                  </p>
                ) : null}

                <div className="rule-meta">
                  <span>{rule.collection.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  <span>
                    {(() => {
                      // Extract name and year from citation (e.g., "oppenheim1956" -> "Oppenheim (1956)")
                      const match = rule.citation.match(/^([a-zA-Z]+)(\d{4})$/);
                      if (match) {
                        const name = match[1].charAt(0).toUpperCase() + match[1].slice(1);
                        const year = match[2];
                        return `${name} (${year})`;
                      }
                      return rule.citation;
                    })()}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Show more button */}
          {hasMore && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                marginTop: 'var(--space-4)',
                border: '1px solid var(--stone-medium)',
                borderRadius: 'var(--radius-md)',
                background: 'transparent',
                color: 'var(--lapis-lazuli)',
                fontSize: 'var(--fs-sm)',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--stone-light)';
                e.currentTarget.style.borderColor = 'var(--lapis-lazuli)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'var(--stone-medium)';
              }}
            >
              Show {filteredRules.length - INITIAL_DISPLAY_COUNT} more{' '}
              {filteredRules.length - INITIAL_DISPLAY_COUNT === 1 ? 'interpretation' : 'interpretations'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
